import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import BookingModal from "@/components/BookingModal";
import { Avatar, TrustBadge, Stars, VerifiedTick, rupee } from "@/components/Bits";
import { MapPin, MessageCircle, Share2, ShieldCheck, BadgeCheck, Award, Umbrella, Check, X, Home, Building2, Video } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOTS = ["Morning", "Afternoon", "Evening"];
const VERIF = {
  aadhaar: { icon: ShieldCheck, t: "Aadhaar e-KYC", d: "Identity confirmed via UIDAI API — not self-declared." },
  police: { icon: BadgeCheck, t: "Police Clearance", d: "Criminal background check via AuthBridge." },
  cert: { icon: Award, t: "BMC Certified", d: "Competency-tested by BookMyCarer." },
  insured: { icon: Umbrella, t: "Insured", d: "Personal accident cover via platform insurance partner." },
};

export default function CarerProfile() {
  const { id } = useParams();
  const [carer, setCarer] = useState(null);
  const [booking, setBooking] = useState(null);

  useEffect(() => { api.get(`/carers/${id}`).then((r) => setCarer(r.data)).catch(() => setCarer(false)); }, [id]);

  if (carer === null) return <PublicLayout><div className="bmc-wrap py-24 text-center text-ink-soft">Loading...</div></PublicLayout>;
  if (carer === false) return <PublicLayout><div className="bmc-wrap py-24 text-center">Carer not found.</div></PublicLayout>;

  const modes = carer.delivery_modes || [];

  return (
    <PublicLayout>
      <div className="bmc-wrap py-10 grid lg:grid-cols-[340px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 self-start space-y-4">
          <div className="card-bmc p-6 text-center">
            <Avatar initials={carer.avatar_initials} gradient={carer.gradient} size={88} />
            <div className="flex items-center justify-center gap-2 mt-4">
              <h1 className="text-2xl font-bold">{carer.name}</h1>
              {carer.verification_status === "Verified" && <VerifiedTick />}
            </div>
            <p className="text-coral font-semibold">{carer.role}</p>
            <p className="text-sm text-ink-soft flex items-center justify-center gap-1 mt-1"><MapPin size={13} /> {carer.area || carer.city} · {carer.exp_years} yrs exp</p>
            <p className="text-xs text-ink-faint mt-1">{(carer.languages || []).join(" · ")}</p>

            <div className="flex items-center justify-center gap-2 mt-4"><Stars rating={carer.rating} size={16} /><span className="text-xs text-ink-soft">({carer.reviews_count} reviews)</span></div>

            <div className="bg-ivory-2 rounded-r p-4 mt-5">
              <span className="font-display font-bold text-3xl">{rupee(carer.rate)}</span>
              <span className="text-sm text-ink-soft">{carer.unit}</span>
            </div>

            <button onClick={() => setBooking(carer)} className="btn-pill bg-coral text-white w-full justify-center py-3 mt-4" data-testid="profile-book">Book Now</button>
            <a href="https://wa.me/919000000000?text=Hi+BookMyCarer" target="_blank" rel="noreferrer" className="btn-pill bg-[#25D366] text-white w-full justify-center py-3 mt-2"><MessageCircle size={16} /> Enquire via WhatsApp</a>
            <button className="text-sm text-ink-soft mt-3 inline-flex items-center gap-1.5 hover:text-coral"><Share2 size={14} /> Share profile</button>
          </div>
        </aside>

        {/* Main */}
        <div className="space-y-10">
          <Section title="About">
            <p className="text-ink-soft leading-relaxed">{carer.bio}</p>
          </Section>

          <Section title="Verification">
            <div className="grid sm:grid-cols-2 gap-4">
              {(carer.badges || []).map((b) => {
                const v = VERIF[b]; if (!v) return null; const I = v.icon;
                return (
                  <div key={b} className="card-bmc p-4 flex gap-3">
                    <span className="grid place-items-center w-10 h-10 rounded-xl bg-sage-soft text-sage-deep shrink-0"><I size={18} /></span>
                    <div><p className="font-semibold text-sm">{v.t}</p><p className="text-xs text-ink-soft mt-0.5">{v.d}</p></div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Care experience">
            <div className="flex flex-wrap gap-2">
              {(carer.conditions || []).map((c) => <span key={c} className="bg-coral-soft text-coral-deep text-sm px-3 py-1.5 rounded-full">{c}</span>)}
            </div>
          </Section>

          <Section title="Experience & credentials">
            <ul className="space-y-2">
              {(carer.credentials || []).map((c) => <li key={c} className="flex items-center gap-2 text-sm text-ink-soft"><Check size={15} className="text-sage" /> {c}</li>)}
            </ul>
          </Section>

          <Section title="Availability">
            <div className="overflow-x-auto">
              <table className="text-xs">
                <thead><tr><th className="p-2"></th>{DAYS.map((d) => <th key={d} className="p-2 font-semibold text-ink-soft">{d}</th>)}</tr></thead>
                <tbody>
                  {SLOTS.map((s) => (
                    <tr key={s}>
                      <td className="p-2 font-semibold text-ink-soft text-right">{s}</td>
                      {DAYS.map((d) => {
                        const on = (carer.availability?.[d] || []).includes(s);
                        return <td key={d} className="p-1"><span className={`block w-9 h-7 rounded ${on ? "bg-coral" : "bg-ivory-2"}`} /></td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Delivery modes">
            <div className="flex gap-6">
              {[["home", Home, "At Home"], ["clinic", Building2, "At Clinic"], ["online", Video, "Online"]].map(([k, I, l]) => (
                <div key={k} className="flex items-center gap-2 text-sm">
                  <I size={18} className={modes.includes(k) ? "text-sage" : "text-ink-faint"} />
                  <span className={modes.includes(k) ? "text-ink" : "text-ink-faint line-through"}>{l}</span>
                  {modes.includes(k) ? <Check size={14} className="text-sage" /> : <X size={14} className="text-ink-faint" />}
                </div>
              ))}
            </div>
          </Section>

          <Section title={`Reviews (${(carer.reviews || []).length})`}>
            <div className="space-y-4">
              {(carer.reviews || []).map((r) => (
                <div key={r.id} className="card-bmc p-4">
                  <Stars rating={r.rating} />
                  <p className="text-sm text-ink mt-2">{r.text}</p>
                  <p className="text-xs text-ink-faint mt-2">{r.reviewer_name} · {r.city} · {r.date}</p>
                </div>
              ))}
              {(!carer.reviews || carer.reviews.length === 0) && <p className="text-sm text-ink-soft">No reviews yet.</p>}
            </div>
          </Section>
        </div>
      </div>

      <BookingModal carer={booking} open={!!booking} onClose={() => setBooking(null)} />
    </PublicLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      {children}
    </section>
  );
}
